import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "reactstrap";
import { ThreeDots } from "react-loader-spinner";

import downloadFile from "../../utils/downloadUtils";
import ImageModal from "./ImageModal";
import limitCapture from "./captureLimiter";
import {
  captureScreenshots,
  describeRequestError,
  describeScreenshotError,
  loadStoredImages,
} from "./screenshotOperations";

/**
 * Per-camera single screenshot with inline preview. Captures through a one-camera screenshot
 * operation, then renders the main image, any per-View sub-images (fisheye), a polygon overlay
 * toggle and a click-to-zoom modal. A batch capture run by the parent lands through `batchResult`.
 */
export default function CameraScreenshot({ storeId, cameraPublicId, capturable, label, batchResult, downloadName }) {
  const [status, setStatus] = useState("idle"); // idle | load | success | fail
  const [images, setImages] = useState(null);
  const [refreshAllowedAt, setRefreshAllowedAt] = useState(null);
  const [showPolygons, setShowPolygons] = useState(false);
  const [modalUri, setModalUri] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showError, setShowError] = useState(false);
  const [copied, setCopied] = useState(false);

  // Bumped on every new capture, so a catalogue read that started before it cannot put the
  // previous frame back on screen.
  const captureVersion = useRef(0);
  const latestImages = useRef(null);
  const showImages = (next) => {
    latestImages.current = next;
    setImages(next);
  };

  const applyResult = (result) => {
    setStatus(result.status);
    setShowError(false);
    setCopied(false);
    if (result.status === "success") {
      captureVersion.current += 1;
      showImages(result.images);
      setRefreshAllowedAt(result.refreshAllowedAt ? new Date(result.refreshAllowedAt) : null);
      setErrorMessage(null);
    } else if (result.status === "fail") {
      setErrorMessage(result.error);
    }
  };

  useEffect(() => {
    if (batchResult) applyResult(batchResult);
  }, [batchResult]);

  // The server replays the last capture until refresh_allowed_at: re-enable the button then.
  const [, setTick] = useState(0);
  const refreshWithheld = Boolean(refreshAllowedAt && refreshAllowedAt > new Date());
  useEffect(() => {
    if (!refreshWithheld) return undefined;
    const timer = setTimeout(() => setTick((tick) => tick + 1), refreshAllowedAt - new Date());
    return () => clearTimeout(timer);
  }, [refreshWithheld, refreshAllowedAt]);

  const capture = async () => {
    setStatus("load");
    setErrorMessage(null);
    setShowError(false);
    setCopied(false);
    try {
      const outcomes = await limitCapture(() => captureScreenshots(storeId, [cameraPublicId]));
      applyResult(outcomes[cameraPublicId] || { status: "fail", error: describeScreenshotError(null) });
    } catch (ex) {
      setErrorMessage(describeRequestError(ex));
      setStatus("fail");
    }
  };

  // Presigned URLs expire after 60 s, and an image never shown yet (the polygon variant, the
  // full-size modal) is not in the browser cache: re-sign before showing one. On failure the
  // current URLs are kept, which still work within their first minute.
  const freshImages = async () => {
    const version = captureVersion.current;
    try {
      const fresh = (await loadStoredImages(storeId, [cameraPublicId]))[cameraPublicId];
      if (captureVersion.current !== version) return latestImages.current;
      if (fresh?.main) {
        showImages(fresh);
        return fresh;
      }
    } catch {
      // Keep the current URLs.
    }
    return images;
  };

  const mainOf = (current, polygons) => (polygons && current.mainPolygons ? current.mainPolygons : current.main);
  const subOf = (current, index, polygons) => {
    const sub = current.subs[index];
    return polygons && sub?.polygons ? sub.polygons : sub?.raw;
  };

  const togglePolygons = async () => {
    await freshImages();
    setShowPolygons((shown) => !shown);
  };

  const openModal = async (pick) => setModalUri(pick(await freshImages()));

  // Downloaded as a blob so the cross-origin S3 URL is saved under our filename instead of being
  // opened in place of the page.
  const download = async () => {
    try {
      const url = mainOf(await freshImages(), showPolygons);
      if (!url) return;
      // An expired URL (the re-sign above failed) answers with an S3 error, not an image.
      const response = await fetch(url);
      if (!response.ok) throw new Error(`storage answered ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      downloadFile(objectUrl, downloadName);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (ex) {
      setErrorMessage(`Download failed: ${ex?.message || "unexpected error"}.`);
      setStatus("fail");
    }
  };

  const copyError = async () => {
    try {
      await navigator.clipboard.writeText(errorMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (permissions/insecure context): the text stays selectable.
    }
  };

  const mainSrc = images ? mainOf(images, showPolygons) : null;
  const hasPolygons = Boolean(images && (images.mainPolygons || images.subs.some((sub) => sub.polygons)));

  return (
    <div className="camera-screenshot">
      <Button
        size="sm"
        className="btn-info"
        onClick={capture}
        disabled={status === "load" || !capturable || refreshWithheld}
        title={refreshWithheld ? `Next capture possible at ${refreshAllowedAt.toLocaleTimeString()}` : undefined}
      >
        {status === "load" ? (
          <ThreeDots height="12" width="18" radius="4" color="#ffffff" ariaLabel="loading" visible />
        ) : (
          "Screenshot"
        )}
      </Button>
      {status === "fail" && (
        <>
          <span className="text-danger ml-2">failed</span>
          {errorMessage && (
            <Button size="sm" color="link" className="text-danger p-0 ml-2" onClick={() => setShowError((v) => !v)}>
              {showError ? "hide error" : "show error"}
            </Button>
          )}
        </>
      )}

      {status === "fail" && showError && errorMessage && (
        <div className="d-flex align-items-start mt-2" style={{ gap: 6 }}>
          <pre
            className="text-danger mb-0"
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              userSelect: "text",
              fontSize: "0.75rem",
              background: "rgba(220,53,69,0.08)",
              border: "1px solid rgba(220,53,69,0.35)",
              borderRadius: 4,
              padding: "6px 8px",
              maxWidth: 480,
              maxHeight: 160,
              overflow: "auto",
              flex: 1,
            }}
          >
            {errorMessage}
          </pre>
          <Button size="sm" color="secondary" outline title="Copy error message" onClick={copyError}>
            <i className={`fas ${copied ? "fa-check" : "fa-copy"}`} />
          </Button>
        </div>
      )}

      {/* A capture can wait minutes in the queue: keep the last image on screen meanwhile and after a failure. */}
      {images && (
        <div className="mt-2">
          <img
            src={mainSrc}
            alt={`${label} screenshot`}
            style={{ width: 200, cursor: "pointer" }}
            onClick={() => openModal((current) => mainOf(current, showPolygons))}
          />
          {images.subs.map((_sub, index) => {
            const src = subOf(images, index, showPolygons);
            return (
              <img
                key={`${cameraPublicId}-view-${index + 1}`}
                src={src}
                alt={`${label} view ${index + 1}`}
                style={{ width: 120, cursor: "pointer", marginLeft: 8 }}
                onClick={() => openModal((current) => subOf(current, index, showPolygons))}
              />
            );
          })}
          {hasPolygons && (
            <Button size="sm" color="secondary" outline className="ml-2" onClick={togglePolygons}>
              <i className="fas fa-draw-polygon mr-1" />
              {showPolygons ? "Hide polygons" : "Show polygons"}
            </Button>
          )}
          {mainSrc && (
            <Button
              size="sm"
              color="secondary"
              outline
              className="ml-2"
              title="Download this screenshot"
              onClick={download}
            >
              <i className="fas fa-download mr-1" />
              Download
            </Button>
          )}
        </div>
      )}

      {modalUri && <ImageModal showModal={Boolean(modalUri)} dataUri={modalUri} toggle={() => setModalUri(null)} />}
    </div>
  );
}

CameraScreenshot.propTypes = {
  storeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  cameraPublicId: PropTypes.string.isRequired,
  // Active with an inference machine; the server rejects the capture otherwise.
  capturable: PropTypes.bool,
  label: PropTypes.string,
  batchResult: PropTypes.shape({ status: PropTypes.string.isRequired }),
  // Filename used when downloading the captured frame (e.g. "cam_ab12.jpg").
  downloadName: PropTypes.string,
};

CameraScreenshot.defaultProps = {
  capturable: true,
  batchResult: null,
  downloadName: "",
};
